import React from 'react';
import { useNavigate } from 'react-router-dom';
import CreditIcon from '../CreditIcon';
import InjuryIcon from '../InjuryIcon';
import beltImg from '../../assets/me_title.png';
import { getBrandBadgeClass } from '../../utils/brandBadge';
import { getFactionMemberContribution } from '../../utils/factionScoring';
import { getEffectiveFactionMemberStatus } from '../../utils/factionLineupRole';

function WrestlerGrid({
  members,
  faction,
  roster = null,
  factionName,
  searchTerm = '',
  scoringStarted = true
}) {
  const navigate = useNavigate();

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusOrder = {
    Main_Eventer: 0,
    In_Ring: 1,
    Backstage: 2
  };

  const preparedMembers = filteredMembers.map(member => ({
    ...member,
    effectiveStatus: getEffectiveFactionMemberStatus({
      faction,
      wrestlerId: member.id,
      fallbackStatus: member.status || null
    })
  }));

  const sortedMembers = [...preparedMembers].sort((a, b) => {
    const orderA = statusOrder[a.effectiveStatus] ?? 3;
    const orderB = statusOrder[b.effectiveStatus] ?? 3;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });

  const membersWithDisplay = sortedMembers.map(member => {
    const currentScore = member.score ?? member.currentScore ?? 0;
    const displayScore = scoringStarted
      ? getFactionMemberContribution({
          faction,
          roster,
          currentScore,
          startScore: member.startScore || 0,
          accumulatedPoints: member.accumulatedPoints || 0,
          status: member.effectiveStatus
        })
      : 0;

    return {
      ...member,
      status: member.effectiveStatus,
      displayScore,
      isMainEventer: member.effectiveStatus === 'Main_Eventer',
      isBackstage: member.effectiveStatus === 'Backstage'
    };
  });

  if (membersWithDisplay.length === 0) {
    return (
      <div className="text-center py-5 bg-light rounded-3">
        <i className="bi bi-emoji-frown display-4 text-muted mb-3"></i>
        <h4 className="text-muted">
          {searchTerm ? 'Nessun wrestler trovato' : 'Nessun membro disponibile'}
        </h4>
        <p className="text-muted mt-2">
          {searchTerm ? 'Prova con un nome diverso' : 'Aggiungi nuovi membri al tuo roster'}
        </p>
      </div>
    );
  }

  return (
    <div className="row g-1">
      {membersWithDisplay.map(member => (
        <div
          key={member.id}
          className="col-6 col-sm-4 col-md-3 col-lg-2"
          role="button"
          onClick={() => navigate(`/wrestlers/${member.id}`)}
        >
          <div
            className={[
              'card h-100 shadow-sm transition-all hover-shadow wrestler-grid-card',
              member.isMainEventer ? 'wrestler-grid-card--main-eventer' : '',
              member.isBackstage ? 'wrestler-grid-card--backstage' : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="position-relative overflow-hidden wrestler-grid-card__media">
              <div className="img-wrapper position-relative">
                {member.isInjured && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      zIndex: 10
                    }}
                  >
                    <InjuryIcon />
                  </div>
                )}

                {member.championImages && member.championImages.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 15,
                      zIndex: 3,
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={member.championImages[0]}
                      alt={member.championTypes ? member.championTypes[0] : 'Champion'}
                      style={{ height: '25px', objectFit: 'contain' }}
                    />
                  </div>
                )}

                <div className="wrestler-grid-card__score">
                  <span className="fw-bold text-brand-secondary-light wrestler-grid-card__score-value">
                    {member.displayScore > 0 ? `${member.displayScore}` : member.displayScore}
                  </span>
                  {member.isMainEventer && (
                    <img
                      src={beltImg}
                      alt=""
                      aria-hidden="true"
                      className="wrestler-grid-card__belt"
                    />
                  )}
                </div>

                <img
                  src={member.imageUrl || '/placeholder-wrestler.jpg'}
                  alt={member.name}
                  className={[
                    'img-fluid w-100 bg-brand-dark wrestler-grid-card__image',
                    member.isBackstage ? 'wrestler-grid-card__image--backstage' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    height: '160px',
                    objectFit: 'cover',
                    objectPosition: 'top center',
                    zIndex: 1
                  }}
                />

                <div className="position-absolute bottom-0 start-0 end-0 p-2 name-overlay">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <h6 className="text-white text-truncate mb-0 fw-semibold">{member.name}</h6>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span
                      className="text-money fw-bold px-1"
                      style={{
                        fontSize: '0.9rem',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <CreditIcon className="me-1" size={16} />
                      {member.cost ?? member.price ?? 0}
                    </span>
                    <span
                      className={`${getBrandBadgeClass(member.brand)} badge px-1 py-1 fw-bold`}
                      style={{ fontSize: '0.75rem', borderRadius: '6px' }}
                    >
                      {member.brand}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default WrestlerGrid;
